import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'
import {Applicant, ContactFlagField, ContactFlagReason} from '~/src/models/applicant'

/**
 * Request body for updating applicant cleanup date
 */
export interface UpdateCleanupDateRequest {
  /** The new cleanup date to set for the applicant (ISO date-time string) */
  lastCleanUpDate: string
}

/**
 * STFFLG-3778 — request body for marking a candidate's contact info invalid.
 * `field` is optional (BE defaults to phone) so the same call flags email/address later.
 */
export interface MarkContactInvalidRequest {
  field?: ContactFlagField
  reason?: ContactFlagReason
  note?: string
}

/**
 * Response from get data cleanup totals API
 */
export interface DataCleanupTotalsResponse {
  thirtyDayRangeCount: number
  sixtyDayRangeCount: number
  ninetyDayRangeCount: number
  ninetyPlusDayRangeCount: number
  /** STFFLG-3778: candidates flagged with invalid contact info (5th "Invalid Information" bucket). */
  invalidCount: number
  totalCount: number
}

/**
 * Response from update cleanup API
 */
export interface UpdateCleanupDateResponse {
  id: string
  lastCleanUpDate: string
  message?: string
}

/**
 * Data Cleanup API Service
 *
 * Provides methods for managing applicant data cleanup operations.
 * Uses the new `/applicants/update-cleanup/{id}` endpoint for updating cleanup dates.
 */
class DataCleanupApiService extends BaseApi {
  /**
   * Update applicant cleanup date with agency validation
   *
   * Updates the lastCleanUpDate field of an applicant after validating agency access.
   *
   * @param applicantId - The ID of the applicant to update
   * @param lastCleanUpDate - The new cleanup date to set (ISO date-time string)
   * @returns Promise with the updated applicant data
   *
   * @example
   * ```typescript
   * await dataCleanupApi.updateApplicantCleanupDate(
   *   'applicant-123',
   *   new Date().toISOString()
   * )
   * ```
   */
  async updateApplicantCleanupDate(
    applicantId: string,
    lastCleanUpDate: string,
  ): Promise<UpdateCleanupDateResponse> {
    try {
      const response = await this.put<UpdateCleanupDateResponse>(
        `/applicants/update-cleanup/${applicantId}`,
        {lastCleanUpDate},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating applicant cleanup date:', error)
      throw error
    }
  }

  /**
   * Update applicant cleanup date to current time
   *
   * Convenience method that sets the cleanup date to the current timestamp.
   *
   * @param applicantId - The ID of the applicant to update
   * @returns Promise with the updated applicant data
   */
  async markApplicantAsCleaned(applicantId: string): Promise<UpdateCleanupDateResponse> {
    return this.updateApplicantCleanupDate(applicantId, new Date().toISOString())
  }

  /**
   * Skip a candidate in the data cleanup process
   *
   * Marks a candidate as skipped with optional notes for follow-up.
   *
   * @param applicantId - The ID of the applicant to skip
   * @param notes - Notes explaining why the candidate was skipped
   * @returns Promise with the updated data cleanup
   */
  async skipCandidate(applicantId: string, notes: string): Promise<unknown> {
    try {
      const response = await this.post<unknown>(`/dataCleanup/skipCandidate`, {
        applicantId,
        notes,
      })
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error skipping candidate:', error)
      throw error
    }
  }

  /**
   * Get data cleanup totals
   *
   * Returns the count of applicants in each cleanup range (30, 60, 90 days)
   *
   * @param agencyId - Optional agency ID (required for General Admin role)
   * @returns Promise with the cleanup totals
   *
   * @example
   * ```typescript
   * const totals = await dataCleanupApi.getDataCleanupTotals('agency-123')
   * // { thirtyDayRangeCount: 5, sixtyDayRangeCount: 10, ninetyDayRangeCount: 15, totalCount: 30 }
   * ```
   */
  async getDataCleanupTotals(agencyId?: string): Promise<DataCleanupTotalsResponse> {
    try {
      const url = agencyId
        ? `/applicants/getDataCleanupTotals?agencyId=${agencyId}`
        : '/applicants/getDataCleanupTotals'
      const response = await this.get<DataCleanupTotalsResponse>(url)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching data cleanup totals:', error)
      throw error
    }
  }

  /**
   * STFFLG-3778 — Mark a candidate's contact information invalid.
   *
   * The candidate leaves the staleness age buckets and moves into the "Invalid
   * Information" bucket until resolved.
   *
   * @param applicantId - The ID of the applicant to flag
   * @param request - Which field (defaults to phone), the reason, and an optional note
   */
  async markContactInvalid(
    applicantId: string,
    request: MarkContactInvalidRequest,
  ): Promise<Applicant> {
    try {
      const response = await this.put<Applicant>(
        `/applicants/mark-contact-invalid/${applicantId}`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error marking candidate contact info invalid:', error)
      throw error
    }
  }

  /**
   * STFFLG-3778 — Resolve a candidate's invalid-contact flag.
   *
   * Clears the flag and resets the cleanup clock, so the candidate re-enters the
   * recently-cleaned (0-30 day) bucket.
   *
   * @param applicantId - The ID of the applicant to resolve
   */
  async resolveContactFlag(applicantId: string): Promise<Applicant> {
    try {
      const response = await this.put<Applicant>(
        `/applicants/resolve-contact-flag/${applicantId}`,
        {},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error resolving candidate contact flag:', error)
      throw error
    }
  }

  /**
   * STFFLG-3778 — Log a call attempt on a flagged candidate (breadcrumb).
   *
   * @param applicantId - The ID of the applicant a call was attempted for
   */
  async logCallAttempt(applicantId: string): Promise<Applicant> {
    try {
      const response = await this.put<Applicant>(`/applicants/log-call-attempt/${applicantId}`, {})
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error logging candidate call attempt:', error)
      throw error
    }
  }
}

// Export singleton instance
export const dataCleanupApi = new DataCleanupApiService()

export default dataCleanupApi
